import { ApiClient } from '../api';
import { ApplicationContext } from '../components/context/application.context';
import { UserContext } from '../components/context/user.context';
import { Injectable, Optional } from '../components/dependency-injection/decorators';
import { AdSlotContainerItemViewEvent } from '../components/event-manager/event/ad-slot-container-item-view.event';
import { AdSlotContainerLifecycleEvent } from '../components/event-manager/event/ad-slot-container-lifecycle.event';
import { AdSlotContainerNotFoundEvent } from '../components/event-manager/event/ad-slot-container-not-found.event';
import { AdSlotContainerPreRenderEvent } from '../components/event-manager/event/ad-slot-container-pre-render.event';
import { AdSlotContainerViewEvent } from '../components/event-manager/event/ad-slot-container-view.event';
import { Events } from '../components/event-manager/event/events';
import { Logger } from '../components/logger/logger';
import { AdSlotContainerItemResolver } from '../components/resolver/ad-slot-container-item.resolver';
import { EventTypeEnum, NpiDto } from '../dto/recs.dto';
import { EventSubscriberInterface } from '../interfaces/components/event-manager/subscriber/event-subscriber.interface';

@Injectable()
export class AdSlotContainerSubscriber implements EventSubscriberInterface {
  public constructor (
    private readonly apiClient: ApiClient,
    private readonly adSlotElementItemResolver: AdSlotContainerItemResolver,
    private readonly userContext: UserContext,
    private readonly applicationContext: ApplicationContext,
    @Optional() private readonly logger?: Logger,
  ) {
  }

  public getSubscribedEvents (): { [key in Events]?: string } {
    return {
      [ Events.AD_SLOT_CONTAINER_PRE_RENDER ]: 'onPreRender',
      [ Events.AD_SLOT_CONTAINER_POST_RENDER ]: 'onPostRender',
      [ Events.AD_SLOT_CONTAINER_NOT_FOUND ]: 'onNotFound',
      [ Events.AD_SLOT_CONTAINER_ITEM_VIEW ]: 'onItemView',
      [ Events.AD_SLOT_CONTAINER_VIEW ]: 'onView',
    };
  }

  public async onPreRender (event: AdSlotContainerPreRenderEvent): Promise<void> {
    const adSlotContainer = event.getAdSlotContainer();
    const adSlot = event.getAdSlotContainer()?.getAdSlot();

    if (!adSlotContainer || !adSlot) {
      return;
    }

    const recommendations = await this.apiClient.getRecommendations(adSlot.getId(), {
      npis: this.userContext.getNpis().filter((npi) => npi.getEncryptedNumber() !== null).map((npi) => ({
        encryptedNumber: npi.getEncryptedNumber(),
        source: npi.getSource(),
        destination: npi.getDestination(),
      } as NpiDto)),
      sourceMetadata: {
        title: this.applicationContext.getMetadata()?.getTitle() ?? undefined,
        doi: this.applicationContext.getMetadata()?.getDoi() ?? undefined,
      },
      startTime: this.applicationContext.getStartTime(),
    });

    for (const recommendationItem of recommendations) {
      adSlotContainer.addItem(this.adSlotElementItemResolver.resolve(recommendationItem));
    }

    event.setItems(recommendations);
  }

  public async onPostRender (event: AdSlotContainerLifecycleEvent): Promise<void> {
    this.logger?.debug(`Ad slot Element for Ad Slot ${event.getAdSlotContainer()?.getAdSlot()?.getId()} onPostRender`);

    const adSlot = event.getAdSlotContainer()?.getAdSlot();

    if (!adSlot) {
      return;
    }

    await this.apiClient.registerEvent(adSlot.getId(), {
      eventType: EventTypeEnum.AD_SLOT_LOAD,
      placementIndex: event.getAdSlotContainer()?.getIndex() ?? 0,
      startTime: this.applicationContext.getStartTime(),
      npis: this.userContext.getNpis().filter((npi) => npi.getEncryptedNumber() !== null).map((npi) => ({
        encryptedNumber: npi.getEncryptedNumber(),
        source: npi.getSource(),
        destination: npi.getDestination(),
      } as NpiDto)),
    });
  }

  public async onNotFound (event: AdSlotContainerNotFoundEvent): Promise<void> {
    this.logger?.debug(`AdSlotElement id ${event.getId()} not found. Used datasetId ${event.getDatasetId()}`);
  }

  public async onItemView (event: AdSlotContainerItemViewEvent): Promise<void> {
    const adSlot = event.getAdSlotContainerItem().getAdSlotContainer()?.getAdSlot();
    const impressionUrl = event.getAdSlotContainerItem().getImpressionUrl();

    if (!adSlot) {
      this.logger?.error(`AdSlot not set for AdSlotContainerItem with id ${event.getAdSlotContainerItem().getId()}`);
      return;
    }

    if (!impressionUrl) {
      this.logger?.error(`Url not set for AdSlotContainerItem with id ${event.getAdSlotContainerItem().getId()}`);
      return;
    }

    const payload = impressionUrl.replace(/\/$/, '').split('/').pop() ?? '';

    await this.apiClient.registerEvent(
      adSlot.getId(),
      {
        eventType: EventTypeEnum.AD_SLOT_ITEM_IMPRESSION,
        placementIndex: event.getAdSlotContainerItem().getAdSlotContainer()?.getIndex() ?? 0,
        encryptedPayload: payload,
        startTime: this.applicationContext.getStartTime(),
      },
    );
    this.logger?.debug(`AdSlotContainerItem with id ${event.getAdSlotContainerItem().getId()} viewed`);
  }

  public async onView (event: AdSlotContainerViewEvent): Promise<void> {
    const adSlot = event.getAdSlotContainer()?.getAdSlot();

    if (!adSlot) {
      this.logger?.error(`AdSlot not set for AdSlotContainer with index ${event.getAdSlotContainer().getIndex()}`);
      return;
    }

    await this.apiClient.registerEvent(
      adSlot.getId(),
      {
        eventType: EventTypeEnum.AD_SLOT_VIEW,
        placementIndex: event.getAdSlotContainer()?.getIndex() ?? 0,
        startTime: this.applicationContext.getStartTime(),
        npis: this.userContext.getNpis().filter((npi) => npi.getEncryptedNumber() !== null).map((npi) => ({
          encryptedNumber: npi.getEncryptedNumber(),
          source: npi.getSource(),
          destination: npi.getDestination(),
        } as NpiDto)),
      },
    );

    this.logger?.debug(`AdSlotContainer with index ${event.getAdSlotContainer().getIndex()} viewed`);
  }
}
